import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import AsyncSelect from 'react-select/async';
import type { GroupBase } from 'react-select';
import { debounce } from 'lodash';
import 'react-datepicker/dist/react-datepicker.css';

import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';

/* ----------------------------- DTOs & Props ----------------------------- */

interface MaintenanceTaskDTO {
  id: number;
  description: string;
  // other fields if needed
}

interface User {
  id: number;
  empNumber: string;
  name: string;
}

interface Props {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

/* -------------------------- Types -------------------------- */

type OptionType = { value: string; label: string; data: any };

/* -------------------------- User Helpers -------------------------- */

const mapApiUserToDTO = (u: any): User | null => {
  if (!u) return null;
  const id = Number(u?.id ?? u?.empNumber);
  if (Number.isNaN(id)) return null;

  return {
    id,
    empNumber: String(u?.empNumber ?? '').trim(),
    name: String(u?.name ?? 'Unknown').trim(),
  };
};

const toOption = (data: any, type: 'user' | 'task'): OptionType => {
  if (type === 'user' && data) {
    const emp = data.empNumber;
    const label = emp ? `${emp} - ${data.name}` : `${data.id} - ${data.name}`;
    return { value: String(data.id), label, data };
  }
  if (type === 'task' && data) {
    return { value: String(data.id), label: `${data.id} - ${data.description}`, data };
  }
  return { value: '', label: '', data: null };
};

const isMinorStaff = (u: any): boolean => {
  const g = String(u?.group ?? '').replace(/[_\s-]/g, '').toUpperCase();
  const roles = Array.isArray(u?.roles)
    ? u.roles.map((r: any) => String(r).replace(/[_\s-]/g, '').toUpperCase())
    : [];
  return g === 'MINORSTAFF' || roles.includes('MINORSTAFF');
};

const isServiceManager = (u: any): boolean => {
  const g = String(u?.group ?? '').replace(/[_\s-]/g, '').toUpperCase();
  const roles = Array.isArray(u?.roles)
    ? u.roles.map((r: any) => String(r).replace(/[_\s-]/g, '').toUpperCase())
    : [];
  return g === 'SERVICEMANAGER' || roles.includes('SERVICEMANAGER');
};

const fetchUsersPaged = async (
  payload: { filterType: string; filterValue: string },
  maxPages = 5
): Promise<any[]> => {
  const all: any[] = [];
  let page = 1;
  while (page <= maxPages) {
    const res = await apiDefinitions.getUsers(String(page), payload);
    if (res?.data?.status !== 'S1000') break;
    const arr: any[] = Array.isArray(res?.data?.data?.users) ? res.data.data.users : [];
    if (!arr.length) break;
    all.push(...arr);
    page += 1;
  }
  return all;
};

/* --------------------------- Async Loaders --------------------------- */

const loadMinorStaffOptions = async (inputValue: string): Promise<OptionType[]> => {
  const q = inputValue.trim();
  const payload = { filterType: q ? 'nameOrEmpNo' : '', filterValue: q };
  const users = await fetchUsersPaged(payload, 5);

  const seen = new Set<number>();
  return users
    .filter(isMinorStaff)
    .map(mapApiUserToDTO)
    .filter((u): u is User => !!u)
    .filter(u => {
      if (seen.has(u.id)) return false;
      seen.add(u.id);
      return true;
    })
    .map(u => toOption(u, 'user'));
};

const loadServiceManagerOptions = async (inputValue: string): Promise<OptionType[]> => {
  const q = inputValue.trim();
  const payload = { filterType: q ? 'nameOrEmpNo' : '', filterValue: q };
  const users = await fetchUsersPaged(payload, 5);

  const seen = new Set<number>();
  return users
    .filter(isServiceManager)
    .map(mapApiUserToDTO)
    .filter((u): u is User => !!u)
    .filter(u => {
      if (seen.has(u.id)) return false;
      seen.add(u.id);
      return true;
    })
    .map(u => toOption(u, 'user'));
};

const loadMaintenanceTaskOptions = async (inputValue: string): Promise<OptionType[]> => {
  const payload = {
    filterType: inputValue.trim() ? 'description' : '',
    filterValue: inputValue.trim(),
  };

  const options: OptionType[] = [];
  let page = 1;
  const maxPages = 5;

  try {
    while (page <= maxPages) {
      const res = await apiDefinitions.getAllMaintenanceTasks(page, payload);
      if (res?.data?.status !== 'S1000') break;
      const tasks = (res?.data?.data || []) as MaintenanceTaskDTO[];
      if (!tasks.length) break;
      tasks.forEach(task => options.push(toOption(task, 'task')));
      page += 1;
    }
  } catch (e) {
    showError('Failed to load maintenance tasks');
  }
  return options;
};

/* ------------------------------ Main Component ------------------------------ */

const AddJobCardModule: React.FC<Props> = ({ show, onHide, onSuccess }) => {
  const [maintenanceTask, setMaintenanceTask] = useState<MaintenanceTaskDTO | null>(null);
  const [responsibleUsers, setResponsibleUsers] = useState<User[]>([]);
  const [accountableUsers, setAccountableUsers] = useState<User[]>([]);
  const [scheduledDateTime, setScheduledDateTime] = useState<Date | null>(new Date());
  const [scheduledEndDateTime, setScheduledEndDateTime] = useState<Date | null>(null);
  const [comment, setComment] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const resetForm = useCallback(() => {
    setMaintenanceTask(null);
    setResponsibleUsers([]);
    setAccountableUsers([]);
    setScheduledDateTime(new Date());
    setScheduledEndDateTime(null);
    setComment('');
  }, []);

  useEffect(() => {
    if (!show) resetForm();
  }, [show, resetForm]);

  const handleSubmit = async () => {
    if (!checkPermission('ROLE_MANAGE_JOB_CARD')) {
      showError('You do not have permission to create job cards.');
      return;
    }

    if (!maintenanceTask) {
      showError('Please select a Maintenance Task.');
      return;
    }
    if (responsibleUsers.length === 0) {
      showError('Please select at least one Responsible Person.');
      return;
    }
    if (accountableUsers.length === 0) {
      showError('Please select at least one Accountable Person.');
      return;
    }
    if (!scheduledDateTime) {
      showError('Please select a Scheduled Date & Time.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        maintenanceTaskId: maintenanceTask.id,
        responsibleUserIds: responsibleUsers.map(u => u.id),
        accountableUserIds: accountableUsers.map(u => u.id),
        scheduledDateTime: scheduledDateTime.toISOString(),
        scheduledEndDateTime: scheduledEndDateTime ? scheduledEndDateTime.toISOString() : null,
        comment: comment.trim() || null,
      };

      const response = await apiDefinitions.createJobCard(payload);

      if (response?.data?.status === 'S1000') {
        showSuccess('Job card created successfully!');
        onSuccess();
        resetForm();
        onHide();
      } else {
        showError(response?.data?.statusDescription || 'Failed to create job card');
      }
    } catch (error: any) {
      showError(error?.response?.data?.statusDescription || 'Error creating job card');
    } finally {
      setIsSaving(false);
    }
  };

  // Debounced async loaders
  const debouncedLoadTasks = useCallback(
    debounce((input: string, callback: (opts: OptionType[]) => void) => {
      loadMaintenanceTaskOptions(input).then(callback);
    }, 300),
    []
  );

  const debouncedLoadMinorStaff = useCallback(
    debounce((input: string, callback: (opts: OptionType[]) => void) => {
      loadMinorStaffOptions(input).then(callback);
    }, 300),
    []
  );

  const debouncedLoadServiceManagers = useCallback(
    debounce((input: string, callback: (opts: OptionType[]) => void) => {
      loadServiceManagerOptions(input).then(callback);
    }, 300),
    []
  );

  return (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Create New Job Card</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Row>
            {/* Left Column */}
            <Col lg={6}>
              <Form.Group className="mb-4">
                <Form.Label>
                  <strong>Maintenance Task <span className="text-danger">*</span></strong>
                </Form.Label>
                <AsyncSelect<OptionType, false, GroupBase<OptionType>>
                  isClearable
                  cacheOptions
                  defaultOptions
                  loadOptions={debouncedLoadTasks}
                  onChange={(opt) => setMaintenanceTask(opt ? opt.data : null)}
                  value={maintenanceTask ? toOption(maintenanceTask, 'task') : null}
                  placeholder="Search by description or ID..."
                  noOptionsMessage={() => 'No tasks found'}
                  loadingMessage={() => 'Searching tasks...'}
                  isDisabled={isSaving}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>
                  <strong>Responsible Persons (Minor Staff) <span className="text-danger">*</span></strong>
                </Form.Label>
                <AsyncSelect<OptionType, true, GroupBase<OptionType>>
                  isMulti
                  isClearable
                  cacheOptions
                  defaultOptions
                  loadOptions={debouncedLoadMinorStaff}
                  onChange={(opts) => setResponsibleUsers(opts.map((o) => o.data).filter(Boolean))}
                  value={responsibleUsers.map((u) => toOption(u, 'user'))}
                  placeholder="Search and select multiple..."
                  noOptionsMessage={() => 'No minor staff found'}
                  loadingMessage={() => 'Searching staff...'}
                  isDisabled={isSaving}
                  closeMenuOnSelect={false}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                <Form.Text className="text-muted">You can select multiple responsible persons.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>
                  <strong>Accountable Persons (Service Managers) <span className="text-danger">*</span></strong>
                </Form.Label>
                <AsyncSelect<OptionType, true, GroupBase<OptionType>>
                  isMulti
                  isClearable
                  cacheOptions
                  defaultOptions
                  loadOptions={debouncedLoadServiceManagers}
                  onChange={(opts) => setAccountableUsers(opts.map((o) => o.data).filter(Boolean))}
                  value={accountableUsers.map((u) => toOption(u, 'user'))}
                  placeholder="Search and select multiple..."
                  noOptionsMessage={() => 'No service managers found'}
                  loadingMessage={() => 'Searching managers...'}
                  isDisabled={isSaving}
                  closeMenuOnSelect={false}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                <Form.Text className="text-muted">You can select multiple accountable persons.</Form.Text>
              </Form.Group>
            </Col>

            {/* Right Column */}
            <Col lg={6}>
              <Form.Group className="mb-4">
                <Form.Label>
                  <strong>Scheduled Start Date & Time <span className="text-danger">*</span></strong>
                </Form.Label>
                <DatePicker
                  selected={scheduledDateTime}
                  onChange={(date: Date | null) => setScheduledDateTime(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  className="form-control"
                  wrapperClassName="w-100"
                  disabled={isSaving}
                  popperPlacement="top-end"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label><strong>Scheduled End Date & Time (Optional)</strong></Form.Label>
                <DatePicker
                  selected={scheduledEndDateTime}
                  onChange={(date: Date | null) => setScheduledEndDateTime(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  className="form-control"
                  wrapperClassName="w-100"
                  disabled={isSaving}
                  minDate={scheduledDateTime || undefined}
                  popperPlacement="top-end"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label><strong>Comment / Instructions</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add any notes or special instructions..."
                  disabled={isSaving}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? 'Creating...' : 'Create Job Card'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddJobCardModule;